import { Component, OnInit, EventEmitter, Output } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';

import { TimeService, FormService, ErrorTranslationService } from '@bayclubs/utils';
import { BayclubsAlertService } from '@bayclubs/alert';
import { DropdownMultiselectItem } from '@bayclubs/drop-down';

import { campsenum } from '../../../models/enums.generated';
import { CampsEnumTranslationService } from '../../../services/camps-enum-translation.service';
import { CouponApiService } from '../../../services/api/coupon-api.service';
import { camps } from '../../../models/models.generated';
import { TextMaskService } from '@bayclubs/ui-text-mask';

@Component({
    selector: 'app-coupon-generate',
    templateUrl: './coupon-generate.component.html',
    styleUrls: ['./coupon-generate.component.scss'],
    standalone: false
})
export class CouponGenerateComponent implements OnInit {
    public typeDropdownItems: DropdownMultiselectItem[] = [];
    public usageTypeDropdownItems: DropdownMultiselectItem[] = [];
    public form: UntypedFormGroup;
    public context: camps.CouponsContext;

    @Output() public closeModal: EventEmitter<camps.CouponInfo> = new EventEmitter();

    constructor(
        private api: CouponApiService,
        private time: TimeService,
        private alert: BayclubsAlertService,
        private enumTranslations: CampsEnumTranslationService,
        private formBuilder: UntypedFormBuilder,
        public mask: TextMaskService,
        private errorTranslate: ErrorTranslationService
    ) { }

    ngOnInit() {
        this.initContext();
        this.initDropdowns();
        this.initForm();
    }

    private initContext() {
        this.api.context().subscribe(x => {
            this.context = x.value;
        });
    }

    public initDropdowns() {
        this.createCouponTypesItems();
        this.createCouponsUsageTypeItems();
    }

    initForm() {
        this.form = this.formBuilder.group({
            validityEnd: [null, [Validators.required]],
            type: [campsenum.CouponType.Percentage, [Validators.required]],
            value: [null, [Validators.required]],
            reason: [null, [Validators.required]],
            email: [null, [Validators.required]],
            maxValueLimit: [null, [Validators.required]],
            sendImmediatelyToProvidedEmail: [true, []],
            categoryId: [null, [Validators.required]],
            usageType: [campsenum.CouponUsageType.Single, [Validators.required]],
            couponCode: [null, []],
            couponCodeProvidedManually: [true, []]
        });

        this.subscribeToTypeChanges();
        this.subscribeToUsageTypeChanges();
        this.subscribeToCouponCodeProvidedManuallyChanges();
    }

    private subscribeToTypeChanges() {
        this.form.controls['type'].valueChanges.subscribe(newValue => {
            if (this.form.controls['type'].value != newValue) this.form.controls['value'].setValue(null);
        });

        this.form.controls['type'].valueChanges.subscribe(newValue => {
            if (newValue === campsenum.CouponType.Percentage) {
                this.form.get('maxValueLimit').setValidators([Validators.required]);
            } else {
                this.form.get('maxValueLimit').setValidators([]);
            }

            this.form.get('maxValueLimit').updateValueAndValidity();
        });
    }

    private subscribeToUsageTypeChanges() {

        this.form.controls['usageType'].valueChanges.subscribe(newValue => {
            if (newValue === campsenum.CouponUsageType.Single) {
                this.form.get('email').setValidators([Validators.required]);
                this.form.get('couponCode').setValidators([]);
            } else {
                this.form.get('email').setValidators([]);
                this.form.get('couponCode').setValue(null);
                this.form.get('couponCodeProvidedManually').setValue(true);
                this.form.get('couponCode').setValidators([Validators.required]);
            }
            this.form.get('couponCode').updateValueAndValidity();
            this.form.get('email').updateValueAndValidity();
        });
    }

    private subscribeToCouponCodeProvidedManuallyChanges() {
        this.form.controls['couponCodeProvidedManually'].valueChanges.subscribe(newValue => {
            if (newValue === true) {
                this.form.get('couponCode').setValidators([Validators.required]);
            } else {
                this.form.get('couponCode').setValidators([]);
                this.form.get('couponCode').setValue(null);
                this.form.get('couponCode').markAsUntouched();
            }

            this.form.get('couponCode').updateValueAndValidity();
        });
    }

    generate() {
        FormService.markFormAsTouched(this.form);
        if (!this.form.valid) return;

        const request = <camps.CouponGenerateRequest>{
            type: this.form.get('type').value,
            value: this.form.get('value').value,
            email: this.form.get('email').value,
            reason: this.form.get('reason').value,
            sendImmediatelyToProvidedEmail: this.isMultiUse() ? false : true,
            validityEnd: this.time.toBcDate(this.form.get('validityEnd').value),
            maxValueLimit: this.form.get('maxValueLimit').value,
            categoryId: this.form.get('categoryId').value,
            usageType: this.form.get('usageType').value,
            couponCode: this.isMultiUse() ? this.form.get('couponCode').value : null
        };

        this.api.generate(request).subscribe(x => {
            if (x.isSuccess) {
                this.alert.showOperationSuccess();
                this.closeModal.emit(x.value);
            } else {
                this.errorTranslate.translateValidationNotifications(x.validationNotification);
                this.alert.showError(x.validationNotification.errors[0].message);
            }
        });
    }

    public translateType(item: camps.CouponInfo): string {
        return this.enumTranslations.couponType(item.type);
    }

    public isAmount(): boolean {
        return this.form.value.type === campsenum.CouponType.Amount;
    }

    public isPercent(): boolean {
        return this.form.value.type === campsenum.CouponType.Percentage;
    }

    public isFreeDays(): boolean {
        return this.form.value.type === campsenum.CouponType.FreeDays;
    }

    public isMultiUse(): boolean {
        return this.form.value.usageType === campsenum.CouponUsageType.Multiple;
    }

    public isCouponCodeManuallyProvided(): boolean {
        return this.form.value.couponCodeProvidedManually === true;
    }

    private createCouponTypesItems() {
        this.createCouponTypeItem(campsenum.CouponType.Percentage);
        this.createCouponTypeItem(campsenum.CouponType.Amount);
        this.createCouponTypeItem(campsenum.CouponType.FreeDays);
    }

    private createCouponsUsageTypeItems() {
        this.createCouponUsageTypeItem(campsenum.CouponUsageType.Single);
        this.createCouponUsageTypeItem(campsenum.CouponUsageType.Multiple);
    }

    private createCouponTypeItem(type) {
        this.typeDropdownItems.push(this.createDropdownItem(type, type, false, campsenum.CouponType[type]));
    }

    private createCouponUsageTypeItem(type) {
        this.usageTypeDropdownItems.push(this.createDropdownItem(type, type, false, campsenum.CouponUsageType[type]));
    }

    private createDropdownItem(id, value, isSelected: boolean = false, displayValue: string = null): DropdownMultiselectItem {
        return <DropdownMultiselectItem>{
            id: id,
            value: value,
            selected: isSelected,
            displayName: displayValue
        };
    }

    public cancel() {
        this.closeModal.emit(null);
    }
}
